import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ApiError } from "@/api/client";
import { useAuth } from "@/api/auth";
import { Panel } from "@/components/Panel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  SET_N_MAX, SET_N_MIN, ZAEHLHINWEIS_MAX, ZAEHLWEISEN, useSetZaehlweise, useZaehlweise,
  type ZaehlweiseAntwort, type ZaehlweiseCode,
} from "@/api/zaehlweise";

// Plan 2026-10-20 (B4): „Wie viele Stueck ergibt ein Lieferanten-Set?" je Grundartikel.
// Der Faktor verschiebt Menge und EK neuer Belegzeilen — darum Aendern nur mit Preisrecht.

function fmtZeit(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short", timeZone: "Europe/Berlin" });
}

function Stand({ daten }: { daten: ZaehlweiseAntwort }) {
  if (daten.zaehlweise === null || daten.stueck_je_set === null) {
    return <p className="text-xs text-ink-2">Nicht festgelegt — Set-Zeilen aus Lieferanten-Dateien bleiben gesperrt.</p>;
  }
  const teile = [`Stück je Lieferanten-Set: ${daten.stueck_je_set}`];
  if (daten.quelle) teile.push(daten.quelle === "manuell" ? "von Hand" : daten.quelle);
  if (daten.geaendert_von) teile.push(`geändert von ${daten.geaendert_von} am ${fmtZeit(daten.geaendert_am)}`);
  return <p className="text-xs text-ink-2">{teile.join(" · ")}</p>;
}

export function ZaehlweisePanel({ stockItemId }: { stockItemId: number }) {
  const auth = useAuth();
  const darfAendern = !!auth.user?.permissions?.article_purchase && !!auth.user?.permissions?.article_purchase_prices;
  const { data, isLoading, error } = useZaehlweise(stockItemId);
  const speichern = useSetZaehlweise(stockItemId);

  const [code, setCode] = useState<ZaehlweiseCode | "">("");
  const [n, setN] = useState("");
  const [hinweis, setHinweis] = useState("");

  useEffect(() => {
    if (!data) return;
    setCode(data.zaehlweise ?? "");
    setN(data.zaehlweise === "set_n" && data.stueck_je_set !== null ? String(data.stueck_je_set) : "");
    setHinweis(data.zaehlhinweis ?? "");
  }, [data]);

  // Kein Grundartikel (400): hier gibt es nichts festzulegen.
  if (error instanceof ApiError && error.status === 400) return null;

  const nZahl = Number(n);
  const nGueltig = Number.isInteger(nZahl) && nZahl >= SET_N_MIN && nZahl <= SET_N_MAX;
  const geaendert = !!data && (
    code !== (data.zaehlweise ?? "")
    || (code === "set_n" && String(data.stueck_je_set ?? "") !== n)
    || hinweis.trim() !== (data.zaehlhinweis ?? "")
  );
  const kannSpeichern = darfAendern && geaendert && !speichern.isPending && (code !== "set_n" || nGueltig);

  function absenden() {
    speichern.mutate(
      code === ""
        ? { zaehlweise: null }
        : {
            zaehlweise: code,
            ...(code === "set_n" ? { stueck_je_set: nZahl } : {}),
            zaehlhinweis: hinweis.trim() === "" ? null : hinweis.trim(),
          },
      {
        onSuccess: () => toast.success(code === "" ? "Zählweise entfernt" : "Zählweise gespeichert"),
        onError: (err) => toast.error(err instanceof Error ? err.message : "Speichern fehlgeschlagen"),
      },
    );
  }

  return (
    <Panel title="Zählweise" subtitle="Wie viele Stück ein Lieferanten-„Set“ dieses Artikels ergibt">
      {isLoading ? (
        <p className="text-xs text-ink-2">Lädt …</p>
      ) : !data ? (
        <p className="text-xs text-ink-2">Zählweise nicht verfügbar.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <label className="flex flex-col gap-1 text-xs font-semibold text-ink-2">
            Zählweise
            <select
              aria-label="Zählweise"
              className="h-9 rounded-md border border-line bg-panel px-2 text-sm font-normal text-ink disabled:opacity-60"
              value={code}
              disabled={!darfAendern || speichern.isPending}
              onChange={(e) => setCode(e.target.value as ZaehlweiseCode | "")}
            >
              <option value="">— nicht festgelegt —</option>
              {ZAEHLWEISEN.map((z) => (
                <option key={z.code} value={z.code}>{z.anzeige}</option>
              ))}
            </select>
          </label>
          {code === "set_n" ? (
            <label className="flex flex-col gap-1 text-xs font-semibold text-ink-2">
              Stück je Set ({SET_N_MIN}–{SET_N_MAX})
              <Input
                aria-label="Stück je Set"
                type="number"
                inputMode="numeric"
                min={SET_N_MIN}
                max={SET_N_MAX}
                step={1}
                value={n}
                disabled={!darfAendern || speichern.isPending}
                aria-invalid={n !== "" && !nGueltig}
                onChange={(e) => setN(e.target.value)}
              />
            </label>
          ) : null}
          <label className="flex flex-col gap-1 text-xs font-semibold text-ink-2">
            Zählhinweis
            <Textarea
              aria-label="Zählhinweis"
              maxLength={ZAEHLHINWEIS_MAX}
              value={hinweis}
              disabled={!darfAendern || speichern.isPending || code === ""}
              placeholder={code === "" ? "Erst eine Zählweise wählen" : "z. B. Paar = 1 Stück"}
              onChange={(e) => setHinweis(e.target.value)}
            />
          </label>
          <Stand daten={data} />
          {darfAendern ? (
            <div>
              <Button type="button" size="sm" disabled={!kannSpeichern} onClick={absenden}>
                Zählweise speichern
              </Button>
            </div>
          ) : (
            <p className="text-xs text-ink-2">Ändern nur mit Einkaufs- und Preisrecht.</p>
          )}
        </div>
      )}
    </Panel>
  );
}
